/* eslint-disable react/no-danger */
import { makeWidgetProps } from 'Src/types/widget-helpers';
import React, { Fragment, type CSSProperties, type ElementType } from 'react';
import { isEmpty } from 'lodash';
import { classnames } from 'Utils/classnames/classnames.utils';
import { COLORS } from 'Components/utils/color/colors';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { Button } from 'Components/frameworks/button/Button';
import type { CountdownProps } from 'Components/frameworks/countdown/Countdown';
import { Countdown } from 'Components/frameworks/countdown/Countdown';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Heading } from 'Components/frameworks/heading/Heading';
import { Text } from 'Components/frameworks/text/Text';
import { isPBFLandingPage } from 'Tracking/utils';
import { useRouter } from 'next/router';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { usePageContext } from 'Context/PageContext';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import type { HeroSize } from './heroUtils';
import {
  ICON_POSITIONS,
  LINK_SPACINGS,
  TITLE_POSITIONS,
  generateBackgroundStylesAndClassName,
  getHeroDimensions,
} from './heroUtils';
type Positions =
  | 'top-left'
  | 'top-middle'
  | 'top-right'
  | 'center-left'
  | 'center-middle'
  | 'center-right'
  | 'bottom-left'
  | 'bottom-middle'
  | 'bottom-right';
export type Props = {
  alt?: string;
  as?: ElementType;
  backgroundURLDesktop?: string;
  backgroundURLMobile?: string;
  className?: string;
  contentPosition?: Positions;
  countdown?: Partial<CountdownProps>;
  hasOverlap?: boolean;
  hLevel?: number;
  icon?: {
    alt: string;
    desktopWidth: number;
    mobileWidth: number;
    displayDesktop: boolean;
    displayMobile: boolean;
    linkMobile: string;
    linkDesktop: string;
    positionMobile: Positions;
    positionDesktop: Positions;
  };
  id?: string;
  isExclusiveCollection?: boolean;
  isImmersive?: boolean;
  //
  link?: {
    label?: string;
    url: string;
  };
  size?: HeroSize;
  style?: CSSProperties;
  subtitle?: string;
  theming?: {
    countdownColor?: Colors;
    linkBackgroundColor?: Colors;
    linkColor?: Colors;
    subtitleColor?: Colors;
    titleColor?: Colors;
  };
  title?: string;
  trackingData?: {
    actionGA: string;
  };
  url?: string;
};
export const Hero = ({
  alt,
  as = DynamicLink,
  backgroundURLDesktop = '',
  backgroundURLMobile = '',
  className,
  contentPosition,
  countdown,
  hasOverlap,
  hLevel = 1,
  link,
  icon,
  id,
  isImmersive = false,
  size = 'large',
  style,
  subtitle,
  theming = makeWidgetProps<Props['theming']>({}),
  title = '',
  trackingData,
  url = '',
}: Props) => {
  const track = useTracking();
  const { isTablet, isDesktop } = useResponsive();
  const { asPath } = useRouter();
  const { trackEvent } = useTrackEvent();
  const { siteSection } = usePageContext();
  // just in case, you know, someone forgets to set one or something
  // mobile is default because Mobile First™
  // we're keeping the support for the legacy url prop for backwards compat
  // i.e. with ImmersiveSlideshow, as providing only a desktop url or a mobile one is pretty awkward
  const { style: styleTag, className: imageClassName } = generateBackgroundStylesAndClassName({
    backgroundURLDesktop: backgroundURLDesktop || url,
    backgroundURLMobile: backgroundURLMobile || url,
  });
  const {
    titleColor = COLORS.WHITE,
    subtitleColor = COLORS.WHITE,
    countdownColor = COLORS.WHITE,
    linkColor = COLORS.BLACK,
    linkBackgroundColor = COLORS.SAFFRON,
  } = theming;
  const { heightClassName, paddingBottomClassName, paddingTopClassName } = getHeroDimensions(size);
  const isHighlight = size === 'highlightSmall' || size === 'highlight';
  const textAlignment = contentPosition
    ? TITLE_POSITIONS[contentPosition]
    : TITLE_POSITIONS['bottom-left'];
  const horizontalAlignment = contentPosition ? contentPosition.split('-')[1] : '';
  const linkSpacing = horizontalAlignment ? LINK_SPACINGS[horizontalAlignment] : '';
  const tracking = {
    actionGA: 'immersive',
    labelGA: `${title}`.trim(),
    ...trackingData,
  };
  const TitleAndSubtitleWrapperTag = link?.url ? as : Fragment;
  const titleAndSubtitleWrapperProps = link?.url
    ? {
        className: classnames(
          'inline-block transform transition-all duration-250 ease-in hover:scale-105',
          linkSpacing,
        ),
        'data-testid': isImmersive ? 'immersive-text' : undefined,
        onClick: () => track(tracking),
        url: link.url,
      }
    : {};

  const getTitleAndSubtitleWrapperProps = () => {
    if (!link?.url) {
      return;
    }

    if (as === 'a') {
      return { ...titleAndSubtitleWrapperProps, href: link?.url };
    }

    return { ...titleAndSubtitleWrapperProps, url: link?.url };
  };

  const buttonTrack = (trackingPayload) => {
    const isPBFLanding = isPBFLandingPage(asPath);

    if (siteSection === 'Shopping_Landing' && isPBFLanding) {
      trackEvent(TRACK_EVENT_NAMES.CLICK_PREBOOKING_FORM_PREREG, {
        detail_click: 'check_my_request',
      });
    } else {
      track && track(trackingPayload);
    }
  };

  const displayIcon = isDesktop ? icon?.displayDesktop : icon?.displayMobile;
  const iconLink =
    (isTablet ? icon?.linkDesktop : icon?.linkMobile) || icon?.linkMobile || icon?.linkDesktop;
  // hey isn't that the same logic that we used up there for the imageUrl?
  // yes it is!
  return (
    <div
      aria-label={alt}
      className={classnames(heightClassName, className)}
      data-size={size}
      data-testid="Hero"
      id={id}
      role="img"
      style={style}
    >
      <div className={classnames('relative', heightClassName, paddingTopClassName)}>
        <div
          className={classnames(
            'animation-duration-500 absolute inset-x-0 top-0 h-full w-full animate-fadeIn bg-cover bg-center bg-no-repeat',
            imageClassName,
            heightClassName,
          )}
        />
        {icon && displayIcon && iconLink && (
          <div
            className={classnames(paddingTopClassName, 'absolute inset-0', {
              'pb-40': hasOverlap,
            })}
          >
            <div
              className={classnames(
                'm-auto flex h-full w-full max-w-site overflow-hidden p-20',
                isTablet
                  ? ICON_POSITIONS[icon.positionDesktop]
                  : ICON_POSITIONS[icon.positionMobile],
              )}
              dir="ltr"
            >
              <img
                alt={icon.alt || ''}
                className="max-h-full flex-none"
                src={iconLink}
                style={{
                  width: isTablet
                    ? `calc(${icon.desktopWidth} / 1180 * 100vw)`
                    : `calc(${icon.mobileWidth} / 375 * 100vw)`,
                  maxWidth: isTablet ? icon.desktopWidth : icon.mobileWidth,
                }}
              />
            </div>
          </div>
        )}
        {!!title && (
          <div
            className={classnames(
              paddingBottomClassName,
              'relative z-1 flex h-full flex-col px-20 pt-10 align-bottom',
            )}
          >
            <div className={classnames(`mx-auto flex w-full max-w-site flex-col ${textAlignment}`)}>
              <TitleAndSubtitleWrapperTag {...getTitleAndSubtitleWrapperProps()}>
                <Heading
                  className={classnames(`text-${titleColor}`, {
                    'lg:text-48': !isHighlight && size !== 'small',
                    'text-shadow-3': titleColor === COLORS.WHITE,
                  })}
                  hLevel={hLevel}
                  theme="heading-brand-1"
                >
                  {title}
                </Heading>
                {!!subtitle && (
                  <Text
                    className={classnames(
                      'mt-5 font-bold text-shadow-3 md:text-16',
                      `text-${subtitleColor}`,
                    )}
                    text={subtitle}
                    theme="text-sans-n1"
                  />
                )}
              </TitleAndSubtitleWrapperTag>
              {!isEmpty(countdown) && (
                <Countdown
                  className="py-10"
                  color={countdownColor}
                  dateTimeEnd={countdown.dateTimeEnd}
                  days={countdown.days}
                  hours={countdown.hours}
                  minutes={countdown.minutes}
                  seconds={countdown.seconds}
                />
              )}
              {link?.label && (
                <div
                  className={classnames({
                    'mt-auto': isHighlight,
                  })}
                >
                  <Button
                    className={classnames(
                      'mt-15 md:mt-0',
                      {
                        'w-full': !isDesktop,
                      },
                      isHighlight && !isDesktop ? 'max-w-none' : 'max-w-280',
                    )}
                    color={linkColor}
                    colorBg={linkBackgroundColor}
                    data-testid={isImmersive ? 'immersive-button' : undefined}
                    isExternalLink={isImmersive}
                    onClick={() => buttonTrack(tracking)}
                    url={link.url || '#hero-beacon'}
                  >
                    {link.label}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        <style
          dangerouslySetInnerHTML={{
            __html: styleTag,
          }}
        />
        {title && (
          <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.2)_100%)]" />
        )}
      </div>
      <div id="hero-beacon" />
    </div>
  );
};
